import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { Download, FileDown } from 'lucide-react';
export const ExportContext = createContext(null);
function useExport() {
    const ctx = useContext(ExportContext);
    if (!ctx)
        throw new Error('ExportContext manquant');
    return ctx;
}
// ---------------------------------------------------------------- Construction
/** Colonne de date d'une courbe, libellée selon le découpage. */
export function dateColumn(granularity) {
    return { key: 'date', label: granularity === 'month' ? 'Mois' : granularity === 'week' ? 'Semaine du' : 'Jour', format: 'date' };
}
/** Tableau des chiffres clés d'un onglet, avec la période précédente en mode comparaison. */
export function kpiTable(rows, compare) {
    const columns = [{ key: 'label', label: 'Indicateur', format: 'text' }, { key: 'value', label: 'Valeur' }];
    if (compare) {
        columns.push({ key: 'previous', label: 'Période précédente' }, { key: 'change', label: 'Évolution (%)', format: 'pct' });
    }
    return {
        title: 'Chiffres clés',
        columns,
        rows: rows.map((r) => ({
            label: r.label,
            value: r.value ?? null,
            previous: r.previous ?? null,
            change: r.value != null && r.previous != null && r.previous !== 0
                ? Math.round(((r.value - r.previous) / r.previous) * 1000) / 10
                : null,
        })),
    };
}
// ---------------------------------------------------------------- Écriture
function cell(value, fmt = 'number', granularity = 'day') {
    if (value === null || value === undefined || value === '')
        return '';
    let out;
    if (fmt === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        out = format(parseISO(value), granularity === 'month' ? 'MM/yyyy' : 'dd/MM/yyyy');
    }
    else if (fmt === 'date' && typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
        out = format(parseISO(`${value}-01`), 'MM/yyyy');
    }
    else if (typeof value === 'number' && fmt !== 'text') {
        out = String(value).replace('.', ',');
    }
    else {
        out = String(value);
    }
    return /[;"\n\r]/.test(out) ? `"${out.replace(/"/g, '""')}"` : out;
}
function tableLines(table) {
    return [
        table.columns.map((c) => cell(c.label, 'text')).join(';'),
        ...table.rows.map((row) => table.columns.map((c) => cell(row[c.key], c.format, table.granularity)).join(';')),
    ];
}
function slugify(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}
function download(filename, content) {
    const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}
function filename(ctx, part) {
    return `statistiques-${ctx.communeSlug}-${slugify(part)}-${ctx.from}_${ctx.to}.csv`;
}
/** En-tête commun : commune, onglet, période. */
function preamble(ctx, title) {
    const period = `Du ${format(parseISO(ctx.from), 'dd/MM/yyyy')} au ${format(parseISO(ctx.to), 'dd/MM/yyyy')}`;
    return [cell(`${ctx.communeName} : ${title}`, 'text'), cell(period, 'text'), ''];
}
// ---------------------------------------------------------------- Boutons
/** Bouton CSV d'un graphique : ses données seules. */
export function CsvButton({ table }) {
    const ctx = useExport();
    const disabled = table.rows.length === 0;
    return (_jsxs("button", { type: "button", disabled: disabled, onClick: () => download(filename(ctx, `${ctx.tabLabel}-${table.title}`), [...preamble(ctx, `${ctx.tabLabel}, ${table.title}`), ...tableLines(table)].join('\r\n')), className: "print:hidden inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed", title: disabled ? 'Aucune donnée à exporter' : `Télécharger les données de « ${table.title} » (CSV)`, children: [_jsx(Download, { size: 12 }), "CSV"] }));
}
/** Barre d'export d'un onglet : CSV de tous ses tableaux, PDF de l'onglet affiché. */
export function TabExport({ tables }) {
    const ctx = useExport();
    const exportCsv = () => {
        const lines = [...preamble(ctx, ctx.tabLabel)];
        for (const table of tables) {
            lines.push(cell(table.title, 'text'), ...tableLines(table), '');
        }
        download(filename(ctx, ctx.tabLabel), lines.join('\r\n'));
    };
    return (_jsxs("div", { className: "print:hidden flex justify-end gap-2 -mt-2 mb-4", children: [_jsxs("button", { type: "button", onClick: exportCsv, className: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50", children: [_jsx(Download, { size: 14 }), "Exporter l'onglet (CSV)"] }), _jsxs("button", { type: "button", onClick: printTab, className: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700", children: [_jsx(FileDown, { size: 14 }), "PDF"] })] }));
}
/**
 * Impression de l'onglet. Les graphiques se redessinent d'abord à la largeur
 * d'une page A4 paysage (classe `stats-printing`, index.css), sinon ils
 * garderaient la largeur de l'écran et seraient coupés.
 */
function printTab() {
    const root = document.documentElement;
    root.classList.add('stats-printing');
    window.dispatchEvent(new Event('resize'));
    window.setTimeout(() => {
        const done = () => {
            root.classList.remove('stats-printing');
            window.dispatchEvent(new Event('resize'));
            window.removeEventListener('afterprint', done);
        };
        window.addEventListener('afterprint', done);
        window.print();
    }, 400);
}
